import {
    Control,
    Controller,
    FieldValues,
    Path,
    RegisterOptions,
} from 'react-hook-form';
import { TextField, TextFieldProps } from '@mui/material';

type ReactHookFormTextFieldProps<
    FormFields extends FieldValues,
    TContext extends object = object,
> = TextFieldProps & {
    control: Control<FormFields, TContext>;
    name: Path<FormFields>;
    readonly?: boolean;
    trimOnBlur?: boolean;
    forceUppercase?: boolean;
    rules?: Omit<
        RegisterOptions<FormFields, Path<FormFields>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
};

export function ReactHookFormTextField<
    FormFields extends FieldValues,
    TContext extends object = object,
>({
    name,
    control,
    readonly,
    rules,
    trimOnBlur,
    forceUppercase,
    onChange: externalChange,
    ...props
}: ReactHookFormTextFieldProps<FormFields, TContext>) {
    return (
        <Controller<FormFields>
            name={name}
            control={control}
            rules={rules}
            render={({
                field: { onChange, value, ref },
                fieldState: { error, isTouched },
            }) => (
                <TextField
                    ref={ref}
                    onChange={event => {
                        if (forceUppercase) {
                            onChange(event.target.value.toUpperCase());
                        } else {
                            onChange(event.target.value);
                        }
                        if (externalChange) {
                            externalChange(event);
                        }
                    }}
                    value={value ?? ''}
                    error={!!error && !isTouched}
                    helperText={error && !isTouched ? error.message : null}
                    disabled={readonly}
                    onBlur={e => {
                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                        if (trimOnBlur) {
                            onChange(value.trim());
                        }
                    }}
                    {...props}
                />
            )}
        />
    );
}
